import { Avatar, CircularProgress, makeStyles, TextField, TextFieldProps } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FilterOptionsState } from '@material-ui/lab/useAutocomplete';
import { isEmail } from 'class-validator';
import React from 'react';

import { ReactComponent as SearchIconSvg } from '../../../../../assets/search.svg';
import { PlexMember, SearchUser, UserSource } from '../../../../../graphql/gen/types';
import { spacings } from '../../../../../theme';
import { usePlexUserSearch, UserSearchState } from '../../../../shared/slate/mentions/usePlexUserSearch';

export type Collaborator = SearchUser;

type Props = {
  onAdd: (collaborator: Collaborator) => void;
  existingMembers: PlexMember[];
  onlySignedUpUsers?: boolean;
  inputProps?: Partial<TextFieldProps>;
};

type Option = Collaborator;

export function NewCollaboratorSearch({
  onAdd,
  existingMembers,
  onlySignedUpUsers = false,
  inputProps = {},
}: Props): React.ReactElement {
  const classes = useStyles();
  const existingMembersMap: { [key: string]: boolean } = {};
  existingMembers?.forEach((member) => {
    existingMembersMap[member.user.email] = true;
  });
  const { query, setQuery, search } = usePlexUserSearch({ onlySignedUpUsers, minimumQueryLength: 3 });

  const handleQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
    setQuery(value);
  };

  const handleChange = (_: React.ChangeEvent<unknown>, value: Option | null): void => {
    const email = value?.email?.trim();
    // add collaborator via email instead of known contact
    // for this we first need to verify the user with the backend
    if (!!email && email === value?.id?.trim()) {
      if (!isEmail(email)) {
        alert(`The email "${email}" is not valid!`);
        return;
      }
    }

    // the collaborator is a known contact, so add the user directly to the list
    if (value != null) {
      onAdd(value);
      setQuery('');
    }
  };

  return (
    <Autocomplete
      clearOnEscape
      noOptionsText={getNoOptionsText(search)}
      style={{ width: 300 }}
      classes={{ inputRoot: classes.searchInput }}
      onChange={handleChange}
      value={null}
      getOptionSelected={(option: Option, value: Option): boolean => {
        return JSON.stringify(option) === JSON.stringify(value);
      }}
      getOptionLabel={(option: Option): string => option.name || option.email}
      loading={isLoading(search)}
      options={search.state === 'done' ? search.options : []}
      renderOption={(option): React.ReactNode => (
        <div className={classes.autocompleteItemContainer}>
          <span className={classes.autocompleteItemLabel}>{option.name || option.email}</span>
          {!!option.picture ? <Avatar src={option.picture} sizes="small" /> : <EmailIcon fontSize="large" />}
        </div>
      )}
      filterOptions={(options: Option[], params: FilterOptionsState<Option>): Option[] => {
        const filtered = options.filter((option) => !existingMembersMap[option.email]);

        // Suggest the invitation of a new collaborator via email
        if (!onlySignedUpUsers && !!params.inputValue && isEmail(params.inputValue)) {
          filtered.push({
            name: `Add "${params.inputValue}"`, // the displayed text (as this user does not have a name anyway)
            email: params.inputValue,
            id: params.inputValue, // must be equal to email so we know to add it by email
            source: UserSource.Plex,
          });
        }

        return filtered;
      }}
      onInputChange={handleQueryChange}
      inputValue={query || ''}
      renderInput={(params: TextFieldProps): React.ReactElement => (
        <div className={classes.inputContainer}>
          <SearchIconSvg className={classes.searchIcon} />
          <TextField
            {...params}
            placeholder={'Search collaborators'}
            {...inputProps}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading(search) ? <CircularProgress color="inherit" size={20} /> : null}
                </React.Fragment>
              ),
            }}
          />
        </div>
      )}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  autocompleteItemContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
  },
  autocompleteItemLabel: {
    display: 'flex',
    flex: 1,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  inputContainer: {
    position: 'relative',
  },
  searchInput: {
    display: 'flex',
    paddingLeft: spacings.xlarge,
  },
  searchIcon: {
    position: 'absolute',
    left: 0,
    width: 18,
    height: 18,
    top: 6,
  },
}));

function isLoading(search: UserSearchState): boolean {
  return search.state === 'can_load' || search.state === 'loading';
}

function getNoOptionsText(search: UserSearchState): string | null {
  switch (search.state) {
    case 'initial':
      return 'Type at least 3 characters to search';
    case 'more_input_needed':
      return 'Type at least 3 characters to search';
    case 'done':
      return 'No results match your query';
    default:
      return null;
  }
}
